import { useState } from "react";
import { ArrowRight, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/api";
import { toast } from "@/components/ui/sonner";

export function CartDrawer() {
  const { items, open, setOpen, removeItem, checkout, checkingOut, promo, setPromo } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const total = items.reduce((sum, item) => sum + Number(item.price || 0), 0);

  async function applyPromo() {
    setApplying(true);
    try {
      const response = await api.post("/promos/validate", { code, items: items.map(({ item_type, item_id }) => ({ item_type, item_id })) });
      setPromo(response.data);
      toast.success(`${response.data.code} applied — ${response.data.description}`);
    } catch (error) {
      toast.error(apiError(error, "Promo code not valid"));
    } finally {
      setApplying(false);
    }
  }

  async function handleCheckout() {
    try {
      await checkout(email);
    } catch (error) {
      toast.error(apiError(error, "Checkout could not be started"));
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full border-l border-gold/20 bg-onyx text-white sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="font-display text-3xl font-light text-white">Your collection</SheetTitle>
          <SheetDescription className="text-zinc-400">Secure Stripe checkout. Instant high-resolution delivery after payment.</SheetDescription>
        </SheetHeader>
        <div className="mt-8 space-y-4">
          {items.length === 0 && <p data-testid="cart-empty-state" className="border border-white/10 p-6 text-sm text-zinc-500">Your collection is empty. Open an event gallery and choose a frame or full album.</p>}
          {items.map((item) => (
            <div key={`${item.item_type}-${item.item_id}`} data-testid={`cart-item-${item.item_id}`} className="flex items-start justify-between gap-4 border border-white/10 bg-white/[0.03] p-4">
              <div>
                <p className="font-lux text-sm uppercase tracking-[0.18em] text-gold">{item.item_type === "album" ? "Full album" : "Single frame"}</p>
                <h3 className="mt-2 font-display text-xl text-white">{item.title}</h3>
                <p className="mt-1 font-mono text-xs text-zinc-500">${Number(item.price).toFixed(2)} USD</p>
              </div>
              <button data-testid={`remove-cart-item-${item.item_id}`} className="p-2 text-zinc-500 transition-colors hover:text-white" onClick={() => removeItem(item)} aria-label="Remove item">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        {!promo ? (
          <div className="mt-6 flex gap-2">
            <Input data-testid="promo-code-input" value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} placeholder="Promo code" className="h-11 border-white/10 bg-white/[0.03] font-mono text-xs uppercase tracking-[0.2em] text-white placeholder:text-zinc-600" />
            <Button data-testid="promo-apply-button" disabled={!code || applying || !items.length} onClick={applyPromo} variant="outline" className="h-11 rounded-none border-gold/40 px-5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-onyx">
              {applying ? "..." : "Apply"}
            </Button>
          </div>
        ) : (
          <div data-testid="promo-applied-chip" className="mt-6 flex items-center justify-between border border-gold/30 bg-gold/10 px-4 py-3">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{promo.code} · {promo.description}</span>
            <button data-testid="promo-remove-button" onClick={() => setPromo(null)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 transition-colors hover:text-white">Remove</button>
          </div>
        )}
        <div className="mt-8 border-t border-gold/20 pt-6">
          <div className="mb-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-display text-2xl text-white">${total.toFixed(2)}</span>
            </div>
            {promo && (
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Discount · {promo.code}</span>
                <span data-testid="cart-discount" className="font-display text-2xl text-gold">-${promo.discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-white/10 pt-3">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Estimated total</span>
              <span data-testid="cart-total" className="font-display text-3xl text-white">${(promo ? promo.estimated_total : total).toFixed(2)}</span>
            </div>
          </div>
          {!user && (
            <Input data-testid="guest-checkout-email-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Email for guest checkout (optional)" className="mb-4 h-12 border-white/10 bg-white/[0.03] text-white placeholder:text-zinc-600" />
          )}
          <Button data-testid="stripe-checkout-button" disabled={!items.length || checkingOut} onClick={handleCheckout} className="h-13 w-full rounded-none bg-gold py-6 font-lux text-sm uppercase tracking-[0.24em] text-onyx transition-colors hover:bg-titanium">
            {checkingOut ? "Preparing checkout" : "Continue to Stripe"} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="mt-4 text-xs leading-relaxed text-zinc-500">Guest checkout is available. Create an account later using the same email to keep downloads in your dashboard.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
